import { AuthClient } from "@supabase/auth-js";
import { fetchWithDeadline } from "./fetchWithDeadline";

/**
 * Auth-only Supabase client, built directly on @supabase/auth-js instead of
 * @supabase/supabase-js's createClient(). createClient() unconditionally
 * bundles GoTrueClient + RealtimeClient + PostgrestClient + StorageClient
 * together (they're required at module scope in SupabaseClient.js, so no
 * tree-shaking lever removes the unused three) — every anonymous visitor to
 * a marketing page was paying for all four just to check "is anyone signed
 * in?" via useZuzuSession. This client only ever needs auth-js, which has no
 * further Supabase dependencies of its own.
 *
 * It must be the ONLY thing that ever calls a session-mutating auth method
 * (signIn/signUp/signOut/setSession/refreshSession/updateUser) anywhere in
 * the app. useZuzuSession's session state is shared, by design, across the
 * whole site+app tree (see the comment at ZuzuAppRuntime's sessionInfo prop:
 * "so there's exactly one auth subscription"); onAuthStateChange only
 * notifies listeners on the SAME instance that performed the mutation
 * (cross-instance sync only happens via the storage event, which browsers
 * fire in *other* tabs, never the one that made the change) — so a mutation
 * fired against a different GoTrueClient instance than the one
 * useZuzuSession listens on would leave sessionInfo stale until a full page
 * reload. See ./client.ts for the full Postgrest/Realtime/Storage client,
 * which stays untouched and is only ever reached through a dynamic import.
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

// Matches the storage key @supabase/supabase-js's createClient() derives
// internally (SupabaseClient.js: `sb-${hostname.split('.')[0]}-auth-token`)
// so this client reads/writes the exact same persisted session as the full
// client in ./client.ts — there is only ever one real session in storage,
// just two doors onto it.
const projectRef = new URL(SUPABASE_URL).hostname.split(".")[0];
const STORAGE_KEY = `sb-${projectRef}-auth-token`;

export const authClient = new AuthClient({
  url: `${SUPABASE_URL}/auth/v1`,
  headers: {
    Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
    apikey: SUPABASE_PUBLISHABLE_KEY,
  },
  storageKey: STORAGE_KEY,
  storage: localStorage,
  persistSession: true,
  autoRefreshToken: true,
  detectSessionInUrl: true,
  // Matters more here than anywhere else: auth-js serialises every session
  // operation behind a lock keyed on the storage key above, acquired with no
  // timeout — and this client shares that key with ./client.ts. A token
  // refresh that hangs therefore blocks every getSession() in the app, and
  // SupabaseClient calls getSession() on each PostgREST request, so the whole
  // data layer stops. A deadline on the refresh makes it fail and release the
  // lock instead of wedging everything behind it.
  fetch: fetchWithDeadline,
});
